import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import { formatToZMW, handlePrint, rootUrl } from '../../../helpers';
import { FaEye, FaPrint, FaSpinner, FaFileExcel, FaFilePdf, FaProjectDiagram, FaFilter, FaCalendarAlt, FaCheckCircle, FaBuilding, FaMoneyBillWave } from 'react-icons/fa';
import { useOutletContext } from 'react-router-dom';
import Modal from '../../../components/Modal';
import { useAuth } from '../../../context/auth-context';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

export default function Projects({startDate, endDate, triggerFetch}) {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [filters, setFilters] = useState({ constituency: '', sub: '', head: '', sof: '', year: '' });
  const {allData, setAllData} = useOutletContext();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [ledgerOpeningBalance, setLedgerOpeningBalance] = useState(0);
  const [ledgerPeriodFrom, setLedgerPeriodFrom] = useState('');
  const [ledgerPeriodTo, setLedgerPeriodTo] = useState('');
  const [reportView, setReportView] = useState('projects'); // 'projects' | 'head'
  const [headCode, setHeadCode] = useState('');
  const [headSub, setHeadSub] = useState('');
  const [headConstituency, setHeadConstituency] = useState('');
  const [headPayee, setHeadPayee] = useState('');
  const [headTransactions, setHeadTransactions] = useState([]);
  const [headLoading, setHeadLoading] = useState(false);
  const [selectedProjectFilter, setSelectedProjectFilter] = useState('');
  const [showBalance, setShowBalance] = useState(true);
  const [showSummaryBalance, setShowSummaryBalance] = useState(true);
  const [loading, setLoading] = useState(false)
  const auth = useAuth()
  const fetchData = async () => {
    
    const { data } = await axios.get(rootUrl(`/getAllProjects/${auth.user.id}~${startDate}~${endDate}`));
    setProjects(data.data);
    setFilteredProjects(data.data);
    setLoading(false)
  };

  useEffect(() => {
    if(triggerFetch && startDate && endDate){
      setLoading(true)
      fetchData();
    }
    fetchData();
  }, [triggerFetch]);

  const componentRef = useRef(null);
  const headComponentRef = useRef(null);

  useEffect(() => {
    const filtered = projects.filter((item) =>
      (filters.constituency === '' || String(item.constituency) === filters.constituency) &&
      (filters.sub === '' || String(item.sub) === filters.sub) &&
      (filters.head === '' || String(item.head) === filters.head) &&
      (filters.sof === '' || item.sof === filters.sof) &&
      (filters.year === '' || String(item.year) === filters.year)
    );
    setFilteredProjects(filtered);
  }, [filters, projects]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    let parsedValue = value;
    if (name === 'sof') parsedValue = value === '' ? '' : value;
    setFilters((prev) => ({ ...prev, [name]: parsedValue }));
  };

  const getUniqueValues = (key) => {
    return [...new Set(projects.map((item) => String(item[key])).filter(Boolean))];
  };

  const groupBy = (array, key) => {
    return array.reduce((result, item) => {
      const groupKey = String(item[key]);
      (result[groupKey] = result[groupKey] || []).push(item);
      return result;
    }, {});
  };

  const getPayeeName = (transaction) =>
    allData?.suppliers?.find((s) => s.id == transaction.paye)?.name || transaction.payeeName || '';

  // When an action button is clicked, fetch the project's transactions and show the modal.
  const handleAction = async (project) => {
    try {
      const response = await axios.get(rootUrl(`/getProjectz/${project.id}~${startDate}~${endDate}`));
      const payload = response.data.data;
      if (Array.isArray(payload)) {
        setTransactions(payload);
        setLedgerOpeningBalance(0);
        setLedgerPeriodFrom(startDate || '');
        setLedgerPeriodTo(endDate || '');
      } else {
        setTransactions(payload?.transactions || []);
        setLedgerOpeningBalance(parseFloat(payload?.openingBalance) || 0);
        setLedgerPeriodFrom(payload?.periodFrom || startDate || '');
        setLedgerPeriodTo(payload?.periodTo || endDate || '');
      }
      setSelectedProject(project);
      setIsModalVisible(true);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  };

  // Filter projects by selected project name before grouping
  const filteredProjectsByName = selectedProjectFilter
    ? filteredProjects.filter(p => p.name === selectedProjectFilter)
    : filteredProjects;
  const groupedByConstituency = groupBy(filteredProjectsByName, 'constituency');

  const exportToExcel = () => {
    if (!selectedProject) return;
    if (!transactions.length && ledgerOpeningBalance === 0) return;
    let runningBalance = ledgerOpeningBalance;
    const rows = [];
    rows.push({
      Date: ledgerPeriodFrom ? ledgerPeriodFrom.substring(0, 10) : '',
      Description: 'Opening balance (brought forward)',
      Payee: '',
      Reference: '',
      Voucher: '',
      Received: '',
      Paid: '',
      Balance: runningBalance,
    });
    transactions.forEach((transaction) => {
      const received = transaction.type === 'income' ? parseFloat(transaction.amount || 0) : 0;
      const paid = transaction.type === 'expense' ? parseFloat(transaction.amount || 0) : 0;
      runningBalance += received - paid;
      rows.push({
        Date: transaction.date?.substring(0, 10) || '',
        Description: transaction.description || '',
        Payee: getPayeeName(transaction),
        Reference: transaction.cheque || '',
        Voucher: transaction.voucher || '',
        Received: received,
        Paid: paid,
        Balance: runningBalance,
      });
    });
    const sheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, 'Transactions');
    const namePart = selectedProject.name ? selectedProject.name.replace(/\s+/g, '_') : selectedProject.id;
    XLSX.writeFile(workbook, `project_${namePart}_transactions.xlsx`);
  };

  const exportLedgerToPdf = () => {
    const input = componentRef.current;
    if (!input || !selectedProject) return;
    html2canvas(input, { scrollY: -window.scrollY }).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      const imgHeight = (canvasHeight * pdfWidth) / canvasWidth;
      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
      heightLeft -= pdfHeight;
      while (heightLeft > 0) {
        position -= pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
        heightLeft -= pdfHeight;
      }
      const namePart = selectedProject.name ? selectedProject.name.replace(/\s+/g, '_') : selectedProject.id;
      pdf.save(`project_ledger_${namePart}.pdf`);
    });
  };

  const headCodeOptions = [...new Set(projects.map((p) => String(p.head)).filter(Boolean))];
  const headSubOptions = [...new Set(
    projects
      .filter((p) => !headCode || String(p.head) === headCode)
      .map((p) => String(p.sub))
      .filter(Boolean)
  )];
  const headConstituencyOptions = [...new Set(projects.map((p) => String(p.constituency)).filter(Boolean))];
  const headPayeeOptions = [...new Set((allData?.suppliers || []).map((s) => String(s.name)).filter(Boolean))];

  const exportHeadTransactionsToExcel = () => {
    if (!headTransactions.length || !headCode) return;
    let runningBalance = 0;
    const rows = headTransactions.map((transaction) => {
      const received = transaction.type === 'income' ? parseFloat(transaction.amount || 0) : 0;
      const paid = transaction.type === 'expense' ? parseFloat(transaction.amount || 0) : 0;
      runningBalance += received - paid;
      return {
        Date: transaction.date?.substring(0, 10) || '',
        Description: transaction.description || '',
        Payee: getPayeeName(transaction),
        Reference: transaction.cheque || '',
        Voucher: transaction.voucher || '',
        Received: received,
        Paid: paid,
        Balance: runningBalance,
      };
    });

    const sheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, 'Transactions');
    XLSX.writeFile(workbook, `head_${String(headCode).replace(/\s+/g, '_')}_transactions.xlsx`);
  };

  const fetchHeadCodeTransactions = async () => {
    try {
      setHeadLoading(true);
      const response = await axios.get(rootUrl('/getProjectHeadTransactions'), {
        params: {
          uid: auth.user.id,
          headCode: headCode || '',
          from: startDate || '',
          to: endDate || '',
          constituency: headConstituency || '',
          subHead: headSub || '',
          payee: headPayee || '',
        },
      });
      setHeadTransactions(response.data.data || []);
    } catch (error) {
      console.error('Error fetching head code transactions:', error);
    } finally {
      setHeadLoading(false);
    }
  };

  return (
    <div className="w-full space-y-6">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className=" px-6 py-4">
          <div className="flex items-center gap-3 mb-2 justify-between">
            <div className="bg-blue-950 rounded-lg p-2">
              <FaProjectDiagram className="text-white text-xl" />
            </div>
            <div>
              <h1 className="text-xl font-bold">
                {reportView === 'projects' ? 'Project Report' : 'Head Code Transactions'}
              </h1>
              <p className="text-gray-600 text-sm">
                {reportView === 'projects'
                  ? 'Comprehensive project financial overview'
                  : 'Transactions filtered by selected head code'}
              </p>
            </div>
            {reportView === 'projects' ? (
              <button
                onClick={() => {
                  setReportView('head');
                  // Close project transactions modal when switching views
                  setIsModalVisible(false);
                  setSelectedProject(null);
                  setTransactions([]);
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-sm whitespace-nowrap"
                title="Show transactions filtered by head code"
              >
                <FaFilter />
                <span className="hidden md:inline">Head Code Transactions</span>
              </button>
            ) : (
              <button
                onClick={() => {
                  setReportView('projects');
                  setHeadCode('');
                  setHeadSub('');
                  setHeadConstituency('');
                  setHeadPayee('');
                  setHeadTransactions([]);
                  setHeadLoading(false);
                }}
                className="bg-blue-950 hover:bg-blue-900 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-sm whitespace-nowrap"
                title="Back to project report"
              >
                <FaProjectDiagram />
                <span className="hidden md:inline">Back to Projects</span>
              </button>
            )}
          </div>
        </div>
        
        <div className="p-6" hidden={reportView !== 'projects'}>
          <div className="bg-gray-50 rounded-lg p-4 mb-6 border border-gray-200">
            <div className="flex items-center gap-2 mb-3">
              <FaFilter className="text-blue-950" />
              <h3 className="font-semibold text-gray-800">Filters</h3>
            </div>
            <div className="flex flex-wrap gap-3 items-center">
              <div className="min-w-[200px] flex-shrink-0">
                <Select
                  classNamePrefix="rs"
                  isClearable
                  placeholder="Filter by project..."
                  options={projects.map(p => ({ value: p.name, label: p.name }))}
                  value={selectedProjectFilter ? { value: selectedProjectFilter, label: selectedProjectFilter } : null}
                  onChange={(opt) => setSelectedProjectFilter(opt ? opt.value : '')}
                />
              </div>
              <label className="flex items-center space-x-2 bg-white px-3 py-2 rounded-md shadow-sm hover:bg-gray-50 cursor-pointer flex-shrink-0 whitespace-nowrap border border-gray-200">
                <input
                  type="checkbox"
                  checked={showBalance}
                  onChange={(e) => setShowBalance(e.target.checked)}
                  className="form-checkbox h-4 w-4 text-blue-600 focus:ring focus:ring-blue-500 focus:ring-opacity-50"
                />
                <span className="text-sm text-gray-700">Show Balance</span>
              </label>
              {/* Year Filter Dropdown */}
              <select
                name="year"
                value={filters.year}
                onChange={handleFilterChange}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[140px] flex-shrink-0 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Years</option>
                {getUniqueValues('year').map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
              {['constituency', 'sof', 'head', 'sub'].map((filterKey) => (
                <select
                  key={filterKey}
                  name={filterKey}
                  value={filters[filterKey]}
                  onChange={handleFilterChange}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[140px] flex-shrink-0 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All {filterKey}</option>
                  {getUniqueValues(filterKey).map((value) => (
                    <option key={value} value={value}>
                      {filterKey === 'sof'
                        ? allData.sof.find((a) => a.id == value)?.name
                        : value}
                    </option>
                  ))}
                </select>
              ))}
            </div>
          </div>
          {
            loading?
            <div className='flex flex-col justify-center items-center h-64 w-full bg-white rounded-2xl border border-gray-100 shadow-sm'>
              <FaSpinner size={50} className="animate-spin text-blue-950 mb-4" />
              <p className="text-gray-600 text-lg">Loading projects...</p>
            </div>:
            <>
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full bg-white">
                  <thead>
                    <tr className="bg-gradient-to-r from-blue-950 to-blue-800 text-white">
                      <th className="px-4 py-3 text-left text-sm font-semibold">Constituency</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">SOF</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Sub</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Name</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Year</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Amount</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Paid</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Received</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Contract Balance</th>
                      {showBalance && <th className="px-4 py-3 text-left text-sm font-semibold">Balance</th>}
                      <th className="px-4 py-3 text-left text-sm font-semibold">Action</th>
                    </tr>
                  </thead>
          <tbody>
            {Object.entries(groupedByConstituency).map(([constituency, projectsByConstituency]) => {
              const groupedByHead = groupBy(projectsByConstituency, 'head');
              const constituencyTotal = projectsByConstituency.reduce(
                (sum, item) => sum + parseFloat(item.amount || 0),
                0
              );
              const constituencyPaid = projectsByConstituency.reduce(
                (sum, item) => sum + parseFloat(item.paid_cash || 0),
                0
              );

              const constituencyRec = projectsByConstituency.reduce(
                (sum, item) => sum + parseFloat(item.rec_cash || 0),
                0
              );
              return (
                <React.Fragment key={constituency}>
                  {Object.entries(groupedByHead).map(([head, projectsByHead]) => {
                    const groupedBySub = groupBy(projectsByHead, 'sub');
                    const headTotal = projectsByHead.reduce(
                      (sum, item) => sum + parseFloat(item.amount || 0),
                      0
                    );
                    const headPaid = projectsByHead.reduce(
                      (sum, item) => sum + parseFloat(item.paid_cash || 0),
                      0
                    );

                    const headRec = projectsByHead.reduce(
                      (sum, item) => sum + parseFloat(item.rec_cash || 0),
                      0
                    );
                    return (
                      <React.Fragment key={head}>
                        {Object.entries(groupedBySub).map(([sub, projectsBySub]) => {
                          const subTotal = projectsBySub.reduce(
                            (sum, item) => sum + parseFloat(item.amount || 0),
                            0
                          );
                          const subPaid = projectsBySub.reduce(
                            (sum, item) => sum + parseFloat(item.paid_cash || 0),
                            0
                          );
                          const subRec = projectsBySub.reduce(
                            (sum, item) => sum + parseFloat(item.rec_cash || 0),
                            0
                          );
                          return (
                            <React.Fragment key={sub}>
                              {projectsBySub.map((item) => (
                                <tr key={item.id} className="border-b border-gray-100 hover:bg-blue-50 transition-colors">
                                  <td className="px-4 py-3 text-sm text-gray-700">{constituency}</td>
                                  <td className="px-4 py-3 text-sm text-gray-700">
                                    {allData.sof.find((a) => a.id === item.sof)?.name || ''}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-700">{head || ''}</td>
                                  <td className="px-4 py-3 text-sm text-gray-700">{sub || ''}</td>
                                  <td className="px-4 py-3 text-sm font-medium text-gray-800">{item.name || ''}</td>
                                  <td className="px-4 py-3 text-sm text-gray-700">{item.year || ''}</td>
                                  <td className="px-4 py-3 text-sm font-medium text-gray-800">{formatToZMW(item.amount)}</td>
                                  <td className="px-4 py-3 text-sm text-red-700">{formatToZMW(item.paid_cash)}</td>
                                  <td className="px-4 py-3 text-sm text-green-700">{formatToZMW(item.rec_cash)}</td>
                                  <td className="px-4 py-3 text-sm font-medium text-gray-800">{formatToZMW(item.amount - parseFloat(item.paid_cash))}</td>
                                  {showBalance && <td className="px-4 py-3 text-sm font-medium text-blue-700">{formatToZMW(item.rec_cash - parseFloat(item.paid_cash))}</td>}
                                  <td className="px-4 py-3 text-sm">
                                    <button
                                      onClick={() => handleAction(item)}
                                      className="p-2 hover:bg-blue-100 rounded-lg transition-colors"
                                      title="View Details"
                                    >
                                      <FaEye className="text-lg text-blue-600 hover:text-blue-800" />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                              <tr className="bg-gradient-to-r from-gray-100 to-gray-50 font-semibold border-t-2 border-gray-300">
                                <td className="px-4 py-3"></td>
                                <td className="px-4 py-3"></td>
                                <td className="px-4 py-3"></td>
                                <td className="px-4 py-3 text-gray-800">Total</td>
                                <td className="px-4 py-3"></td>
                                <td className="px-4 py-3"></td>
                                <td className="px-4 py-3 font-bold text-gray-800">{formatToZMW(subTotal)}</td>
                                <td className="px-4 py-3 font-bold text-red-700">{formatToZMW(subPaid)}</td>
                                <td className="px-4 py-3 font-bold text-green-700">{formatToZMW(subRec)}</td>
                                <td className="px-4 py-3 font-bold text-gray-800">{formatToZMW(subTotal - subPaid)}</td>
                                {showBalance && <td className="px-4 py-3 font-bold text-blue-700">{formatToZMW(subRec - subPaid)}</td>}
                                <td className="px-4 py-3"></td>
                              </tr>
                            </React.Fragment>
                          );
                        })}
                        <tr className="bg-gradient-to-r from-gray-200 to-gray-300 font-bold border-t-2 border-gray-400">
                          <td className="px-4 py-3"></td>
                          <td className="px-4 py-3"></td>
                          <td className="px-4 py-3 text-gray-800">Total</td>
                          <td className="px-4 py-3"></td>
                          <td className="px-4 py-3"></td>
                          <td className="px-4 py-3"></td>
                          <td className="px-4 py-3 text-gray-800">{formatToZMW(headTotal)}</td>
                          <td className="px-4 py-3 text-red-800">{formatToZMW(headPaid)}</td>
                          <td className="px-4 py-3 text-green-800">{formatToZMW(headRec)}</td>
                          <td className="px-4 py-3 text-gray-800">{formatToZMW(headTotal - headPaid)}</td>
                          {showBalance && <td className="px-4 py-3 text-blue-800">{formatToZMW(headRec - headPaid)}</td>}
                          <td className="px-4 py-3"></td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                  <tr className="bg-gradient-to-r from-blue-950 to-blue-800 text-white font-bold border-t-2 border-blue-400">
                    <td className="px-4 py-3 flex items-center gap-2">
                      <FaBuilding />
                      {constituency} Total
                    </td>
                    <td className="px-4 py-3"></td>
                    <td className="px-4 py-3"></td>
                    <td className="px-4 py-3"></td>
                    <td className="px-4 py-3"></td>
                    <td className="px-4 py-3"></td>
                    <td className="px-4 py-3 text-lg">{formatToZMW(constituencyTotal)}</td>
                    <td className="px-4 py-3 text-lg">{formatToZMW(constituencyPaid)}</td>
                    <td className="px-4 py-3 text-lg">{formatToZMW(constituencyRec)}</td>
                    <td className="px-4 py-3 text-lg">{formatToZMW(constituencyTotal - constituencyPaid)}</td>
                    {showBalance && <td className="px-4 py-3 text-lg">{formatToZMW(constituencyRec - constituencyPaid)}</td>}
                    <td className="px-4 py-3"></td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
              </div>
            </>
          }
        </div>
      </div>
      

      {isModalVisible && selectedProject && (
        <Modal
          title="Project ledger"
          onClose={() => {
            setIsModalVisible(false);
            setSelectedProject(null);
            setTransactions([]);
            setLedgerOpeningBalance(0);
            setLedgerPeriodFrom('');
            setLedgerPeriodTo('');
          }}
        >
  <div> 
     <div className="flex items-center justify-between mb-4 bg-gray-50 p-3 rounded-lg">
      <div className="flex gap-2">
      <button
         onClick={() => handlePrint(componentRef)}
         className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-sm"
         title="Print"
       >
         <FaPrint/>
         <span className="hidden md:inline">Print</span>
       </button>
       <button
         onClick={exportToExcel}
        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-sm"
        title="Export to Excel"
       >
         <FaFileExcel/>
         <span className="hidden md:inline">Excel</span>
       </button>
       <button
         type="button"
         onClick={exportLedgerToPdf}
         className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-sm"
         title="Export to PDF"
       >
         <FaFilePdf />
         <span className="hidden md:inline">PDF</span>
       </button>
      </div>
      
       <label className="flex items-center space-x-2 bg-white px-3 py-2 rounded-md shadow-sm hover:bg-gray-50 cursor-pointer border border-gray-200">
         <input
           type="checkbox"
           checked={showSummaryBalance}
           onChange={(e) => setShowSummaryBalance(e.target.checked)}
           className="form-checkbox h-4 w-4 text-blue-600 focus:ring focus:ring-blue-500 focus:ring-opacity-50"
         />
         <span className="text-sm text-gray-700">Show Balance</span>
       </label>
     </div>
          <div ref={componentRef} className="p-4">
            <div className='flex justify-center'>
            <div>
               <img className="w-[150px] h-[150px]" src={rootUrl(`/${allData?.council[0]?.path}`)} alt="Council Logo" />
               <span className="font-bold text-xl">{allData.council[0]?.name.toUpperCase()}</span>
            </div>
            </div>

            <hr className='my-2' />
           
            {/* Project Details Header */}
            <div className="mb-4 border-b pb-2">
              <h2 className="text-2xl font-bold">{selectedProject.name}</h2>
              <p className="text-gray-700">{selectedProject.description || 'No description available.'}</p>
              <div className="mt-2 flex flex-wrap gap-4">
                <div>
                  <strong>Constituency:</strong> {selectedProject.constituency}
                </div>
                <div>
                  <strong>Head:</strong> {selectedProject.head}
                </div>
                <div>
                  <strong>Sub:</strong> {selectedProject.sub}
                </div>
                <div>
                  <strong>SOF:</strong>{' '}
                  {allData.sof.find((a) => a.id === selectedProject.sof)?.name || ''}
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-4">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-blue-950 to-blue-800 text-white">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Summary</th>
                    <th className="px-4 py-3 text-right font-semibold">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-200 hover:bg-gray-50">
                    <th className="px-4 py-3 text-left text-gray-700 font-medium">Contract Sum</th>
                    <td className="px-4 py-3 text-right font-semibold text-gray-800">{formatToZMW(selectedProject.amount)}</td>
                  </tr>
                  <tr className="border-b border-gray-200 hover:bg-gray-50">
                    <th className="px-4 py-3 text-left text-gray-700 font-medium">Paid Amount</th>
                    <td className="px-4 py-3 text-right font-semibold text-red-700">{formatToZMW(selectedProject.paid_cash)}</td>
                  </tr>
                  <tr className="border-b border-gray-200 hover:bg-gray-50">
                    <th className="px-4 py-3 text-left text-gray-700 font-medium">Received Amount</th>
                    <td className="px-4 py-3 text-right font-semibold text-green-700">{formatToZMW(selectedProject.rec_cash)}</td>
                  </tr>
                  <tr className="border-b border-gray-200 hover:bg-gray-50">
                    <th className="px-4 py-3 text-left text-gray-700 font-medium">Contract Balance</th>
                    <td className="px-4 py-3 text-right font-semibold text-gray-800">{formatToZMW(selectedProject.amount - selectedProject.paid_cash)}</td>
                  </tr>
                  {showSummaryBalance && (
                    <tr className="bg-blue-50 border-b border-gray-200">
                      <th className="px-4 py-3 text-left text-gray-700 font-medium">Balance</th>
                      <td className="px-4 py-3 text-right font-bold text-blue-700">{formatToZMW(selectedProject.rec_cash - selectedProject.paid_cash)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {/* Transactions Table — period ledger with opening balance */}
            <div>
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between my-2">
                <div>
                  <h3 className="text-xl font-semibold">Period ledger</h3>
                  <p className="text-sm text-gray-600">
                    Cut-off: <strong>{ledgerPeriodFrom?.substring(0, 10) || '—'}</strong> to{' '}
                    <strong>{ledgerPeriodTo?.substring(0, 10) || '—'}</strong>
                    <span className="text-gray-500"> · Opening balance is net cashbook movement before this period</span>
                  </p>
                </div>
                <label className="flex items-center space-x-2 bg-gray-100 px-3 py-2 rounded-md shadow-sm hover:bg-gray-200 cursor-pointer self-start sm:self-center">
                  <input
                    type="checkbox"
                    checked={showBalance}
                    onChange={(e) => setShowBalance(e.target.checked)}
                    className="form-checkbox h-4 w-4 text-blue-600 focus:ring focus:ring-blue-500 focus:ring-opacity-50"
                  />
                  <span className="text-sm text-gray-700">Show Balance</span>
                </label>
              </div>
              <div className="overflow-x-auto">
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="min-w-full bg-white">
                    <thead>
                      <tr className="bg-gradient-to-r from-blue-950 to-blue-800 text-white">
                        <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Description</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Payee</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Ref</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">PV</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold">Received</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold">Paid</th>
                        {showBalance && <th className="px-4 py-3 text-right text-sm font-semibold">Balance</th>}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="bg-amber-50 border-b border-amber-100">
                        <td className="px-4 py-3 text-sm font-medium text-gray-800">
                          {ledgerPeriodFrom ? ledgerPeriodFrom.substring(0, 10) : '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-800" colSpan={1}>
                          Opening balance (brought forward)
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">—</td>
                        <td className="px-4 py-3 text-sm text-gray-500">—</td>
                        <td className="px-4 py-3 text-sm text-gray-500">—</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-400">—</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-400">—</td>
                        {showBalance && (
                          <td className="px-4 py-3 text-sm text-right font-semibold text-blue-800">
                            {formatToZMW(ledgerOpeningBalance)}
                          </td>
                        )}
                      </tr>
                      {transactions.length === 0 && (
                        <tr>
                          <td className="px-4 py-3 text-sm text-gray-500 italic" colSpan={showBalance ? 8 : 7}>
                            No transactions in this period (opening balance above still applies).
                          </td>
                        </tr>
                      )}
                      {(() => {
                        let runningBalance = ledgerOpeningBalance;
                        return transactions.map((transaction) => {
                          const received = transaction.type === 'income' ? parseFloat(transaction.amount || 0) : 0;
                          const paid = transaction.type === 'expense' ? parseFloat(transaction.amount || 0) : 0;
                          runningBalance += received - paid;
                          return (
                            <tr key={transaction.id} className="border-b border-gray-100 hover:bg-blue-50 transition-colors">
                              <td className="px-4 py-3 text-sm text-gray-700">{transaction.date.substring(0, 10)}</td>
                              <td className="px-4 py-3 text-sm text-gray-700">{transaction.description}</td>
                              <td className="px-4 py-3 text-sm text-gray-700">{getPayeeName(transaction)}</td>
                              <td className="px-4 py-3 text-sm text-gray-700">{transaction.cheque}</td>
                              <td className="px-4 py-3 text-sm text-gray-700">{transaction.voucher}</td>
                              <td className="px-4 py-3 text-sm text-right font-medium text-green-700">{formatToZMW(received)}</td>
                              <td className="px-4 py-3 text-sm text-right font-medium text-red-700">{formatToZMW(paid)}</td>
                              {showBalance && (
                                <td className="px-4 py-3 text-sm text-right font-medium text-blue-700">{formatToZMW(runningBalance)}</td>
                              )}
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gradient-to-r from-gray-700 to-gray-800 text-white font-bold border-t-2 border-gray-400">
                        <td className="px-4 py-3 flex items-center gap-2">
                          <FaMoneyBillWave />
                          Period total
                        </td>
                        <td className="px-4 py-3"></td>
                        <td className="px-4 py-3"></td>
                        <td className="px-4 py-3"></td>
                        <td className="px-4 py-3"></td>
                        <td className="px-4 py-3 text-right text-lg">
                          {formatToZMW(transactions.reduce((acc, curr) => acc + (curr.type === 'income' ? parseFloat(curr.amount || 0) : 0), 0))}
                        </td>
                        <td className="px-4 py-3 text-right text-lg">
                          {formatToZMW(transactions.reduce((acc, curr) => acc + (curr.type === 'expense' ? parseFloat(curr.amount || 0) : 0), 0))}
                        </td>
                        {showBalance && (
                          <td className="px-4 py-3 text-right text-lg">
                            {formatToZMW(
                              ledgerOpeningBalance +
                                transactions.reduce(
                                  (acc, curr) =>
                                    acc +
                                    (curr.type === 'income' ? parseFloat(curr.amount || 0) : 0) -
                                    (curr.type === 'expense' ? parseFloat(curr.amount || 0) : 0),
                                  0
                                )
                            )}
                          </td>
                        )}
                      </tr>
                    </tfoot>
                  </table>
                </div>

                <hr className="my-5" />

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p>
                      <strong>Prepared By:</strong> __________________
                    </p>
                    <p>
                      <strong>Signature:</strong> __________________
                    </p>
                    <p>
                      <strong>Designation:</strong> __________________
                    </p>
                    <p>
                      <strong>Date:</strong> __________________
                    </p>
                  </div>

                  <div>
                    <p>
                      <strong>Verified By:</strong> __________________
                    </p>
                    <p>
                      <strong>Signature:</strong> __________________
                    </p>
                    <p>
                      <strong>Designation:</strong> __________________
                    </p>
                    <p>
                      <strong>Date:</strong> __________________
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>
        </Modal>
      )}

      {reportView === 'head' && (
        <div className="p-6">
          <div>
            <div className="flex items-center justify-between mb-4 bg-gray-50 p-3 rounded-lg">
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setReportView('projects');
                    setIsModalVisible(false);
                    setSelectedProject(null);
                    setTransactions([]);
                    setHeadCode('');
                    setHeadConstituency('');
                    setHeadPayee('');
                    setHeadTransactions([]);
                    setHeadLoading(false);
                  }}
                  className="bg-blue-950 hover:bg-blue-900 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-sm whitespace-nowrap"
                  title="Back to project report"
                >
                  <FaProjectDiagram />
                  <span className="hidden md:inline">Back to Projects</span>
                </button>
                <button
                  onClick={() => handlePrint(headComponentRef)}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-sm"
                  title="Print"
                >
                  <FaPrint />
                  <span className="hidden md:inline">Print</span>
                </button>
                <button
                  onClick={exportHeadTransactionsToExcel}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-sm"
                  title="Export to Excel"
                >
                  <FaFileExcel />
                  <span className="hidden md:inline">Excel</span>
                </button>
              </div>

              <label className="flex items-center space-x-2 bg-white px-3 py-2 rounded-md shadow-sm hover:bg-gray-50 cursor-pointer border border-gray-200">
                <input
                  type="checkbox"
                  checked={showBalance}
                  onChange={(e) => setShowBalance(e.target.checked)}
                  className="form-checkbox h-4 w-4 text-blue-600 focus:ring focus:ring-blue-500 focus:ring-opacity-50"
                />
                <span className="text-sm text-gray-700">Show Balance</span>
              </label>
            </div>

            <div ref={headComponentRef} className="p-4">
              <div className="flex justify-center">
                <div>
                  <img
                    className="w-[150px] h-[150px]"
                    src={rootUrl(`/${allData?.council[0]?.path}`)}
                    alt="Council Logo"
                  />
                  <span className="font-bold text-xl">{allData.council[0]?.name.toUpperCase()}</span>
                </div>
              </div>

              <hr className="my-2" />

              <div className="mb-4 border-b pb-2">
                <h2 className="text-2xl font-bold">Head Code Transactions</h2>
                <p className="text-gray-700">Date range: {startDate || '-'} to {endDate || '-'}</p>
              </div>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="min-w-[260px]">
                  <Select
                    classNamePrefix="rs"
                    isClearable
                    placeholder="Select constituency..."
                    options={headConstituencyOptions.map((value) => ({ value, label: value }))}
                    value={headConstituency ? { value: headConstituency, label: headConstituency } : null}
                    onChange={(opt) => {
                      setHeadConstituency(opt ? opt.value : '');
                      setHeadTransactions([]);
                    }}
                  />
                </div>
                <div className="min-w-[260px]">
                  <Select
                    classNamePrefix="rs"
                    isClearable
                    placeholder="Select head code..."
                    options={headCodeOptions.map((hc) => ({ value: hc, label: hc }))}
                    value={headCode ? { value: headCode, label: headCode } : null}
                    onChange={(opt) => {
                      const next = opt ? opt.value : '';
                      setHeadCode(next);
                      setHeadSub('');
                      setHeadTransactions([]);
                    }}
                  />
                </div>
                <div className="min-w-[260px]">
                  <Select
                    classNamePrefix="rs"
                    isClearable
                    placeholder="Select sub head code..."
                    options={headSubOptions.map((value) => ({ value, label: value }))}
                    value={headSub ? { value: headSub, label: headSub } : null}
                    onChange={(opt) => {
                      setHeadSub(opt ? opt.value : '');
                      setHeadTransactions([]);
                    }}
                  />
                </div>
                <div className="min-w-[260px]">
                  <Select
                    classNamePrefix="rs"
                    isClearable
                    placeholder="Select payee..."
                    options={headPayeeOptions.map((value) => ({ value, label: value }))}
                    value={headPayee ? { value: headPayee, label: headPayee } : null}
                    onChange={(opt) => {
                      setHeadPayee(opt ? opt.value : '');
                      setHeadTransactions([]);
                    }}
                  />
                </div>

                <button
                  onClick={fetchHeadCodeTransactions}
                  className="bg-blue-950 hover:bg-blue-900 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-sm"
                  title="Fetch transactions"
                  disabled={headLoading}
                >
                  {headLoading ? <FaSpinner className="animate-spin" /> : <FaEye />}
                  <span>{headLoading ? 'Loading...' : 'Show Transactions'}</span>
                </button>
              </div>

              {headLoading ? (
                <div className="flex flex-col justify-center items-center h-64 w-full bg-white rounded-2xl border border-gray-100 shadow-sm">
                  <FaSpinner size={50} className="animate-spin text-blue-950 mb-4" />
                  <p className="text-gray-600 text-lg">Loading transactions...</p>
                </div>
              ) : headTransactions.length > 0 ? (
                <div className="overflow-x-auto">
                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="min-w-full bg-white">
                      <thead>
                        <tr className="bg-gradient-to-r from-blue-950 to-blue-800 text-white">
                          <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Description</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Payee</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Ref</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">PV</th>
                          <th className="px-4 py-3 text-right text-sm font-semibold">Received</th>
                          <th className="px-4 py-3 text-right text-sm font-semibold">Paid</th>
                          {showBalance && <th className="px-4 py-3 text-right text-sm font-semibold">Balance</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          let runningBalance = 0;
                          return headTransactions.map((transaction) => {
                            const received = transaction.type === 'income' ? parseFloat(transaction.amount || 0) : 0;
                            const paid = transaction.type === 'expense' ? parseFloat(transaction.amount || 0) : 0;
                            runningBalance += received - paid;
                            return (
                              <tr key={transaction.id} className="border-b border-gray-100 hover:bg-blue-50 transition-colors">
                                <td className="px-4 py-3 text-sm text-gray-700">{transaction.date.substring(0, 10)}</td>
                                <td className="px-4 py-3 text-sm text-gray-700">{transaction.description}</td>
                                <td className="px-4 py-3 text-sm text-gray-700">{getPayeeName(transaction)}</td>
                                <td className="px-4 py-3 text-sm text-gray-700">{transaction.cheque}</td>
                                <td className="px-4 py-3 text-sm text-gray-700">{transaction.voucher}</td>
                                <td className="px-4 py-3 text-sm text-right font-medium text-green-700">{formatToZMW(received)}</td>
                                <td className="px-4 py-3 text-sm text-right font-medium text-red-700">{formatToZMW(paid)}</td>
                                {showBalance && (
                                  <td className="px-4 py-3 text-sm text-right font-medium text-blue-700">
                                    {formatToZMW(runningBalance)}
                                  </td>
                                )}
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                      <tfoot>
                        <tr className="bg-gradient-to-r from-gray-700 to-gray-800 text-white font-bold border-t-2 border-gray-400">
                          <td className="px-4 py-3 flex items-center gap-2">
                            <FaMoneyBillWave />
                            Total
                          </td>
                          <td className="px-4 py-3"></td>
                          <td className="px-4 py-3"></td>
                          <td className="px-4 py-3"></td>
                          <td className="px-4 py-3"></td>
                          <td className="px-4 py-3 text-right text-lg">
                            {formatToZMW(headTransactions.reduce((acc, curr) => acc + (curr.type === 'income' ? parseFloat(curr.amount || 0) : 0), 0))}
                          </td>
                          <td className="px-4 py-3 text-right text-lg">
                            {formatToZMW(headTransactions.reduce((acc, curr) => acc + (curr.type === 'expense' ? parseFloat(curr.amount || 0) : 0), 0))}
                          </td>
                          {showBalance && (
                            <td className="px-4 py-3 text-right text-lg">
                              {formatToZMW(
                                headTransactions.reduce(
                                  (acc, curr) =>
                                    acc +
                                    ((curr.type === 'income' ? parseFloat(curr.amount || 0) : 0) -
                                      (curr.type === 'expense' ? parseFloat(curr.amount || 0) : 0)),
                                  0
                                )
                              )}
                            </td>
                          )}
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  <hr className="my-5" />

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p>
                        <strong>Prepared By:</strong> __________________
                      </p>
                      <p>
                        <strong>Signature:</strong> __________________
                      </p>
                      <p>
                        <strong>Designation:</strong> __________________
                      </p>
                      <p>
                        <strong>Date:</strong> __________________
                      </p>
                    </div>

                    <div>
                      <p>
                        <strong>Checked By:</strong> __________________
                      </p>
                      <p>
                        <strong>Signature:</strong> __________________
                      </p>
                      <p>
                        <strong>Designation:</strong> __________________
                      </p>
                      <p>
                        <strong>Date:</strong> __________________
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <p>No transactions found for the selected filters.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
